/* global BigInt */
import { PublicKey, SystemProgram, SYSVAR_RENT_PUBKEY, Transaction, TransactionInstruction } from '@solana/web3.js';
import { 
  TOKEN_PROGRAM_ID, 
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
  createAssociatedTokenAccountInstruction,
} from '@solana/spl-token';
import { Buffer } from 'buffer';
import bs58 from 'bs58';
import {
  DISCRIMINATORS,
  AUCTION_STATE_LAYOUT,
  POPPER_NFT_LAYOUT,
  PDA_SEEDS,
  AUCTION_CONSTANTS,
} from '../constants/accountLayouts';

// Re-export for backward compatibility
export { AUCTION_CONSTANTS };

// Auction PDA seeds (from centralized constants)
const AUCTION_SEED = Buffer.from(PDA_SEEDS.AUCTION_STATE);
const BID_ESCROW_SEED = Buffer.from(PDA_SEEDS.BID_ESCROW);
const NFT_ESCROW_SEED = Buffer.from(PDA_SEEDS.NFT_ESCROW);
const POPPER_NFT_SEED = Buffer.from(PDA_SEEDS.POPPER_NFT);

// Account discriminators (from centralized constants)
const AUCTION_STATE_DISCRIMINATOR = DISCRIMINATORS.AUCTION_STATE;
const POPPER_NFT_DISCRIMINATOR = DISCRIMINATORS.POPPER_NFT;

/**
 * Parse AuctionState account data manually
 * 
 * AuctionState layout:
 * - 8 bytes: discriminator
 * - 32 bytes: nft_mint (Pubkey)
 * - 32 bytes: seller (Pubkey)
 * - 33 bytes: highest_bidder (Option<Pubkey>: 1 byte discriminator + 32 bytes pubkey)
 * - 8 bytes: highest_bid (u64)
 * - 8 bytes: min_bid_increment (u64)
 * - 8 bytes: starting_bid (u64)
 * - 8 bytes: start_time (i64)
 * - 8 bytes: end_time (i64)
 * - 1 byte: is_finalized (bool)
 * - 1 byte: is_cancelled (bool)
 * - 2 bytes: platform_fee_bps (u16)
 * - 4 bytes: bid_count (u32)
 * - 1 byte: bump (u8)
 * - 64 bytes: reserved
 */
export function parseAuctionState(data) {
  console.log('[Auction] parseAuctionState: data.length =', data.length);
  // Minimum size: discriminator(8) + nft_mint(32) + seller(32) + Option(1) + rest(44) = 117
  // With Option<Pubkey> = Some: add 32 more = 149
  // But account allocates 218 for reserved space
  if (data.length < 117) {
    throw new Error('AuctionState data too short');
  }

  let offset = 8; // Skip discriminator

  const nftMint = new PublicKey(data.slice(offset, offset + 32));
  offset += 32;

  const seller = new PublicKey(data.slice(offset, offset + 32));
  offset += 32;

  // Option<Pubkey>: Borsh serialization
  // - If None: just 1 byte (0x00)
  // - If Some: 1 byte (0x01) + 32 bytes pubkey
  const hasHighestBidder = data[offset] === 1;
  offset += 1;
  let highestBidder = null;
  if (hasHighestBidder) {
    highestBidder = new PublicKey(data.slice(offset, offset + 32));
    offset += 32;
  }
  // NOTE: Do NOT skip 32 bytes when None - Borsh doesn't include them!

  const highestBid = data.readBigUInt64LE(offset);
  offset += 8;

  const minBidIncrement = data.readBigUInt64LE(offset);
  offset += 8;

  const startingBid = data.readBigUInt64LE(offset);
  offset += 8;

  const startTime = Number(data.readBigInt64LE(offset));
  offset += 8;

  // Debug: Log the raw bytes for endTime
  const endTimeBytes = Array.from(data.slice(offset, offset + 8));
  console.log('[Auction] Parse: offset before endTime =', offset, 'data.length =', data.length);
  console.log('[Auction] Parse: bytes at offset 137:', Array.from(data.slice(137, 145)));
  console.log('[Auction] Parse: bytes at current offset:', endTimeBytes);
  const endTime = Number(data.readBigInt64LE(offset));
  console.log('[Auction] Parse endTime: offset =', offset, 'bytes =', endTimeBytes, 'value =', endTime);
  offset += 8;

  const isFinalized = data[offset] === 1;
  offset += 1;

  const isCancelled = data[offset] === 1;
  offset += 1;

  const platformFeeBps = data.readUInt16LE(offset);
  offset += 2;

  const bidCount = data.readUInt32LE(offset);
  offset += 4;

  const bump = data[offset];

  return {
    nftMint,
    seller,
    highestBidder,
    highestBid: Number(highestBid),
    minBidIncrement: Number(minBidIncrement),
    startingBid: Number(startingBid),
    startTime,
    endTime,
    isFinalized,
    isCancelled,
    platformFeeBps,
    bidCount,
    bump,
  };
}

/**
 * Fetch all auction accounts using manual parsing
 * @param {Connection} connection - Solana connection
 * @param {PublicKey} programId - Program ID
 * @returns {Promise<Array>} Array of parsed auction accounts
 */
export async function fetchAllAuctionsManual(connection, programId) {
  try {
    const discBytes = Buffer.from(AUCTION_STATE_DISCRIMINATOR);
    const discBs58 = bs58.encode(discBytes);
    console.log(`[Auction] Searching for AuctionState with discriminator: ${AUCTION_STATE_DISCRIMINATOR} -> bs58: ${discBs58}`);
    console.log(`[Auction] Program ID: ${programId?.toBase58()}`);
    
    const accounts = await connection.getProgramAccounts(programId, {
      commitment: 'confirmed',
      filters: [
        {
          memcmp: {
            offset: 0,
            bytes: discBs58,
          },
        },
      ],
    });

    console.log(`[Auction] Found ${accounts.length} AuctionState accounts`);

    return accounts.map(({ pubkey, account }) => {
      try {
        const parsed = parseAuctionState(account.data);
        return {
          publicKey: pubkey,
          account: parsed,
        };
      } catch (err) {
        console.error(`[Auction] Failed to parse auction ${pubkey.toBase58()}:`, err);
        return null;
      }
    }).filter(Boolean);
  } catch (error) {
    console.error('[Auction] Error fetching auctions:', error);
    return [];
  }
}

/**
 * Fetch active auctions (not finalized, not cancelled, not ended)
 * @param {Connection} connection - Solana connection
 * @param {PublicKey} programId - Program ID
 * @returns {Promise<Array>} Array of active auction accounts
 */
export async function fetchActiveAuctionsManual(connection, programId) {
  const allAuctions = await fetchAllAuctionsManual(connection, programId);
  const now = Math.floor(Date.now() / 1000);
  
  // Debug logging
  allAuctions.forEach((a, i) => {
    console.log(`[Auction] Auction ${i}: isFinalized=${a.account.isFinalized}, isCancelled=${a.account.isCancelled}, endTime=${a.account.endTime}, now=${now}, timeRemaining=${a.account.endTime - now}s`);
  });
  
  return allAuctions.filter(a => 
    !a.account.isFinalized && 
    !a.account.isCancelled && 
    a.account.endTime > now
  );
}

/**
 * Parse PopperNFT account data manually (simplified for auction use)
 * 
 * PopperNFT layout (relevant fields):
 * - 8 bytes: discriminator
 * - 1 byte: version
 * - 32 bytes: creator_wallet
 * - 32 bytes: current_owner
 * - 32 bytes: nft_mint
 * - ... more fields
 */
export function parsePopperNFTBasic(data) {
  if (data.length < 8 + 1 + 32 + 32 + 32) {
    throw new Error('PopperNFT data too short');
  }

  let offset = 8; // Skip discriminator

  const version = data[offset];
  offset += 1;

  const creatorWallet = new PublicKey(data.slice(offset, offset + 32));
  offset += 32;

  const currentOwner = new PublicKey(data.slice(offset, offset + 32));
  offset += 32;

  const nftMint = new PublicKey(data.slice(offset, offset + 32));
  offset += 32;

  // Read vote stats
  const totalVotes = data.readBigUInt64LE(offset);
  offset += 8;

  const totalVoteSol = data.readBigUInt64LE(offset);
  offset += 8;

  const totalVotePoppers = data.readBigUInt64LE(offset);
  offset += 8;

  // Skip votes_per_round, sol_per_round, poppers_per_round (192 bytes total)
  offset += 192;

  const totalSales = data.readBigUInt64LE(offset);
  offset += 8;

  const totalRoyaltiesPaid = data.readBigUInt64LE(offset);
  offset += 8;

  const createdAt = Number(data.readBigInt64LE(offset));
  offset += 8;

  const createdInRound = data[offset];
  offset += 1;

  const lastActiveRound = data[offset];
  offset += 1;

  const isWinner = data[offset] === 1;
  offset += 1;

  const isArchived = data[offset] === 1;
  offset += 1;

  const isVotingEligible = data[offset] === 1;

  return {
    version,
    creatorWallet,
    currentOwner,
    nftMint,
    totalVotes: Number(totalVotes),
    totalVoteSol: Number(totalVoteSol),
    totalVotePoppers: Number(totalVotePoppers),
    totalSales: Number(totalSales),
    totalRoyaltiesPaid: Number(totalRoyaltiesPaid),
    createdAt,
    createdInRound,
    lastActiveRound,
    isWinner,
    isArchived,
    isVotingEligible,
  };
}

/**
 * Fetch PopperNFTs owned by a specific wallet
 * @param {Connection} connection - Solana connection
 * @param {PublicKey} programId - Program ID
 * @param {PublicKey} ownerWallet - Owner wallet address
 * @returns {Promise<Array>} Array of PopperNFT accounts owned by wallet
 */
export async function fetchPopperNFTsByOwner(connection, programId, ownerWallet) {
  try {
    // current_owner is at offset 8 (discriminator) + 1 (version) + 32 (creator) = 41
    const accounts = await connection.getProgramAccounts(programId, {
      filters: [
        {
          memcmp: {
            offset: 0,
            bytes: bs58.encode(Buffer.from(POPPER_NFT_DISCRIMINATOR)),
          },
        },
        {
          memcmp: {
            offset: 41, // current_owner offset
            bytes: ownerWallet.toBase58(),
          },
        },
      ],
    });

    console.log(`[Auction] Found ${accounts.length} PopperNFTs owned by ${ownerWallet.toBase58().slice(0, 8)}...`);

    return accounts.map(({ pubkey, account }) => {
      try {
        const parsed = parsePopperNFTBasic(account.data);
        return {
          publicKey: pubkey,
          account: parsed,
        };
      } catch (err) {
        console.error(`[Auction] Failed to parse PopperNFT ${pubkey.toBase58()}:`, err);
        return null;
      }
    }).filter(Boolean);
  } catch (error) {
    console.error('[Auction] Error fetching user NFTs:', error);
    return [];
  }
}

/**
 * Derive Auction PDA
 * @param {PublicKey} programId - Popperverse program ID
 * @param {PublicKey} nftMint - NFT mint address
 * @param {PublicKey} seller - Seller wallet address
 * @returns {Promise<[PublicKey, number]>} [auctionPDA, bump]
 */
export async function getAuctionPDA(programId, nftMint, seller) {
  return await PublicKey.findProgramAddress(
    [AUCTION_SEED, nftMint.toBuffer(), seller.toBuffer()],
    programId
  );
}

/**
 * Derive Bid Escrow PDA
 * @param {PublicKey} programId - Popperverse program ID
 * @param {PublicKey} auctionState - Auction state PDA
 * @returns {Promise<[PublicKey, number]>} [bidEscrowPDA, bump]
 */
export async function getBidEscrowPDA(programId, auctionState) {
  return await PublicKey.findProgramAddress(
    [BID_ESCROW_SEED, auctionState.toBuffer()],
    programId
  );
}

/**
 * Derive NFT Escrow PDA
 * @param {PublicKey} programId - Popperverse program ID
 * @param {PublicKey} auctionState - Auction state PDA
 * @returns {Promise<[PublicKey, number]>} [nftEscrowPDA, bump]
 */
export async function getNFTEscrowPDA(programId, auctionState) {
  return await PublicKey.findProgramAddress(
    [NFT_ESCROW_SEED, auctionState.toBuffer()],
    programId
  );
}

/**
 * Derive PopperNFT PDA
 * @param {PublicKey} programId - Popperverse program ID
 * @param {PublicKey} nftMint - NFT mint address
 * @returns {Promise<[PublicKey, number]>} [popperNftPDA, bump]
 */
export async function getPopperNFTPDA(programId, nftMint) {
  return await PublicKey.findProgramAddress(
    [POPPER_NFT_SEED, nftMint.toBuffer()],
    programId
  );
}

/**
 * Create an auction instruction (raw instruction building - no Anchor dependency)
 * @param {Object} params
 * @param {PublicKey} params.programId - Popperverse program ID
 * @param {PublicKey} params.seller - Seller wallet
 * @param {PublicKey} params.nftMint - NFT mint address
 * @param {PublicKey} params.nftTokenAccount - Seller's NFT token account
 * @param {number} params.startingBid - Starting bid in SOL
 * @param {number} params.durationDays - Auction duration in days
 * @param {number} params.minBidIncrement - Minimum bid increment in SOL (default 0.1)
 * @returns {Promise<Transaction>} Transaction to create auction
 */
export async function createAuctionInstruction({
  programId,
  seller,
  nftMint,
  nftTokenAccount,
  startingBid,
  durationDays,
  minBidIncrement = 0.1,
}) {
  const [auctionState] = await getAuctionPDA(programId, nftMint, seller);
  const [popperNft] = await getPopperNFTPDA(programId, nftMint);
  
  // NFT escrow is an Associated Token Account owned by the auction_state PDA
  // This allows the auction_state PDA to sign for transfers from the escrow
  const nftEscrow = getAssociatedTokenAddressSync(
    nftMint,
    auctionState,
    true  // allowOwnerOffCurve = true for PDA owners
  );

  // Build instruction data: discriminator + starting_bid (u64) + duration (i64) + min_increment (u64)
  const discriminator = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.CREATE_AUCTION);
  
  // Convert to lamports as BigInt for proper 64-bit encoding
  const startingBidLamports = BigInt(Math.floor(startingBid * 1e9));
  const durationSeconds = BigInt(Math.floor(durationDays * 24 * 60 * 60));
  const minBidIncrementLamports = BigInt(Math.floor(minBidIncrement * 1e9));
  
  const startingBidBuf = Buffer.alloc(8);
  startingBidBuf.writeBigUInt64LE(startingBidLamports);
  
  const durationBuf = Buffer.alloc(8);
  durationBuf.writeBigInt64LE(durationSeconds);
  
  const minIncrementBuf = Buffer.alloc(8);
  minIncrementBuf.writeBigUInt64LE(minBidIncrementLamports);
  
  const instructionData = Buffer.concat([discriminator, startingBidBuf, durationBuf, minIncrementBuf]);

  // Create the escrow ATA first (owned by auction_state PDA)
  const createEscrowAtaIx = createAssociatedTokenAccountInstruction(
    seller,           // payer
    nftEscrow,        // ata address
    auctionState,     // owner (the auction PDA)
    nftMint           // mint
  );

  const auctionInstruction = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: seller, isSigner: true, isWritable: true },
      { pubkey: nftMint, isSigner: false, isWritable: false },
      { pubkey: nftTokenAccount, isSigner: false, isWritable: true },
      { pubkey: auctionState, isSigner: false, isWritable: true },
      { pubkey: nftEscrow, isSigner: false, isWritable: true },
      { pubkey: popperNft, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
    ],
    data: instructionData,
  });

  // Transaction: 1) Create escrow ATA, 2) Create auction (transfers NFT to escrow)
  const transaction = new Transaction()
    .add(createEscrowAtaIx)
    .add(auctionInstruction);
    
  return transaction;
}

/**
 * Place a bid instruction
 * @param {Object} params
 * @param {Program} params.program - Anchor program instance
 * @param {PublicKey} params.programId - Popperverse program ID
 * @param {PublicKey} params.bidder - Bidder wallet
 * @param {PublicKey} params.auctionState - Auction state PDA
 * @param {PublicKey} params.previousBidder - Previous highest bidder (or bidder if first bid)
 * @param {number} params.bidAmount - Bid amount in SOL
 * @returns {Promise<Transaction>} Transaction to place bid
 */
export async function placeBidInstruction({
  programId,
  bidder,
  auctionState,
  previousBidder,
  bidAmount,
}) {
  const [bidEscrow] = await getBidEscrowPDA(programId, auctionState);
  
  // Build instruction data: discriminator + bid_amount (u64)
  const discriminator = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.PLACE_BID);
  const bidAmountLamports = BigInt(Math.floor(bidAmount * 1e9));
  const bidAmountBuf = Buffer.alloc(8);
  bidAmountBuf.writeBigUInt64LE(bidAmountLamports);
  
  const instructionData = Buffer.concat([discriminator, bidAmountBuf]);

  const instruction = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: bidder, isSigner: true, isWritable: true },
      { pubkey: auctionState, isSigner: false, isWritable: true },
      { pubkey: bidEscrow, isSigner: false, isWritable: true },
      { pubkey: previousBidder, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: instructionData,
  });

  const transaction = new Transaction().add(instruction);
  return transaction;
}

/**
 * Finalize auction instruction (raw instruction building - no Anchor dependency)
 * @param {Object} params
 * @param {PublicKey} params.programId - Popperverse program ID
 * @param {PublicKey} params.finalizer - Wallet finalizing (can be anyone)
 * @param {PublicKey} params.auctionState - Auction state PDA
 * @param {PublicKey} params.nftMint - NFT mint address
 * @param {PublicKey} params.seller - Seller wallet
 * @param {PublicKey} params.sellerNftAccount - Seller's NFT token account
 * @param {PublicKey} params.winner - Winner wallet (highest bidder)
 * @param {PublicKey} params.winnerNftAccount - Winner's NFT token account
 * @param {PublicKey} params.treasury - Treasury wallet
 * @returns {Promise<Transaction>} Transaction to finalize auction
 */
export async function finalizeAuctionInstruction({
  programId,
  finalizer,
  auctionState,
  nftMint,
  seller,
  sellerNftAccount,
  winner,
  winnerNftAccount,
  treasury,
}) {
  const [bidEscrow] = await getBidEscrowPDA(programId, auctionState);
  const [popperNft] = await getPopperNFTPDA(programId, nftMint);
  
  // NFT escrow is an ATA owned by the auction_state PDA
  const nftEscrow = getAssociatedTokenAddressSync(
    nftMint,
    auctionState,
    true  // allowOwnerOffCurve = true for PDA owners
  );

  // Build instruction data: just the discriminator (no args for finalize)
  const instructionData = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.FINALIZE_AUCTION);

  const instruction = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: finalizer, isSigner: false, isWritable: false },
      { pubkey: auctionState, isSigner: false, isWritable: true },
      { pubkey: bidEscrow, isSigner: false, isWritable: true },
      { pubkey: nftEscrow, isSigner: false, isWritable: true },
      { pubkey: nftMint, isSigner: false, isWritable: false },
      { pubkey: seller, isSigner: false, isWritable: true },
      { pubkey: sellerNftAccount, isSigner: false, isWritable: true },
      { pubkey: winner, isSigner: false, isWritable: true },
      { pubkey: winnerNftAccount, isSigner: false, isWritable: true },
      { pubkey: treasury, isSigner: false, isWritable: true },
      { pubkey: popperNft, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data: instructionData,
  });

  const transaction = new Transaction().add(instruction);
  return transaction;
}

/**
 * Cancel auction instruction (raw instruction building - no Anchor dependency)
 * @param {Object} params
 * @param {PublicKey} params.programId - Popperverse program ID
 * @param {PublicKey} params.seller - Seller wallet
 * @param {PublicKey} params.auctionState - Auction state PDA
 * @param {PublicKey} params.nftMint - NFT mint address
 * @param {PublicKey} params.sellerNftAccount - Seller's NFT token account
 * @returns {TransactionInstruction} Instruction to cancel auction
 */
export function cancelAuctionInstruction({
  programId,
  seller,
  auctionState,
  nftMint,
  sellerNftAccount,
}) {
  // NFT escrow is an ATA owned by the auction_state PDA
  const nftEscrow = getAssociatedTokenAddressSync(
    nftMint,
    auctionState,
    true  // allowOwnerOffCurve = true for PDA owners
  );

  // Build instruction data: just the discriminator (no args for cancel)
  const instructionData = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.CANCEL_AUCTION);

  const instruction = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: seller, isSigner: true, isWritable: true },
      { pubkey: auctionState, isSigner: false, isWritable: true },
      { pubkey: nftEscrow, isSigner: false, isWritable: true },
      { pubkey: nftMint, isSigner: false, isWritable: false },
      { pubkey: sellerNftAccount, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data: instructionData,
  });

  return instruction;
}

/**
 * Fetch all active auctions
 * @param {Program} program - Anchor program instance
 * @returns {Promise<Array>} Array of auction accounts
 */
export async function fetchActiveAuctions(program) {
  try {
    const auctions = await program.account.auctionState.all([
      {
        memcmp: {
          offset: 8 + 32 + 32 + 33 + 8 + 8 + 8 + 8 + 8, // Skip to is_finalized
          bytes: Buffer.from([0]).toString('base64'), // false
        },
      },
    ]);
    
    const now = Math.floor(Date.now() / 1000);
    return auctions.filter(a => a.account.endTime > now && !a.account.isCancelled);
  } catch (error) {
    console.error('Error fetching auctions:', error);
    return [];
  }
}

/**
 * Format auction time remaining
 * @param {number} endTime - Unix timestamp
 * @returns {string} Human-readable time remaining
 */
export function formatTimeRemaining(endTime) {
  const now = Math.floor(Date.now() / 1000);
  const remaining = endTime - now;
  
  if (remaining <= 0) return 'Ended';
  
  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Calculate platform fee
 * @param {number} bidAmount - Bid amount in lamports
 * @returns {number} Platform fee in lamports
 */
export function calculatePlatformFee(bidAmount) {
  return Math.floor((bidAmount * AUCTION_CONSTANTS.PLATFORM_FEE_BPS) / 10000);
}

/**
 * Derive Program Authority PDA
 * @param {PublicKey} programId - Program ID
 * @returns {Promise<[PublicKey, number]>} [programAuthorityPDA, bump]
 */
export async function getProgramAuthorityPDA(programId) {
  return await PublicKey.findProgramAddress(
    [Buffer.from('program_authority')],
    programId
  );
}

/**
 * Admin force end auction instruction
 * Sets the auction end_time to current time, allowing immediate finalization
 * @param {Object} params
 * @param {PublicKey} params.programId - Popperverse program ID
 * @param {PublicKey} params.authority - Admin authority wallet
 * @param {PublicKey} params.auctionState - Auction state PDA
 * @returns {Promise<TransactionInstruction>} Instruction to force end auction
 */
export async function adminForceEndAuctionInstruction({
  programId,
  authority,
  auctionState,
}) {
  const [programAuthority] = await getProgramAuthorityPDA(programId);
  
  // Build instruction data: just the discriminator (no args)
  const instructionData = Buffer.from(DISCRIMINATORS.INSTRUCTIONS.ADMIN_FORCE_END_AUCTION);

  const instruction = new TransactionInstruction({
    programId,
    keys: [
      { pubkey: authority, isSigner: true, isWritable: true },
      { pubkey: programAuthority, isSigner: false, isWritable: false },
      { pubkey: auctionState, isSigner: false, isWritable: true },
    ],
    data: instructionData,
  });

  return instruction;
}
